import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'

import { fromJS } from 'immutable'

import { KBarSpecialButton } from '@aytm/lib/kbar'
import TutorialVideoList from '@aytm/lib/rfd-ui/components/advanced/tutorial-video-list'

import StickyHeader from '@aytm/app/stats/page/components/StickyHeader'

import style from '@aytm/app/stats/page/components/styles/ContentHeader.module.scss'

export const StatTutorialVideo = fromJS({
  video: {
    id: 'statistics',
    title: 'Tutorial video',
    length: '8:05',
    autoplay: false,
    code: '501928393',
    url: 'https://player.vimeo.com/video/'
  },
  videoPoints: [
    { time: 0, text: 'Finding your Results page' },
    { time: 27, text: 'Original - as fielded Report' },
    { time: 79, text: 'General analysis tools' },
    { time: 181, text: 'Survey Specifications & Reports' },
    { time: 226, text: 'View Options & Filters' },
    { time: 305, text: 'Data Lab & Data Weighting' },
    { time: 335, text: 'Crosstab & Attachments' },
    { time: 366, text: 'Color Options & Exports' }
  ],
  videoNote: "Learn about aytm's interactive survey results, report, and data analysis tools.",
  videoThumbnail: '/images/tvs/statistics.jpg'
})

const ContentHeader = ({ mode, state, surveyTitle, report, customizations = [], isReportXpert, ...props }) => {
  const stickyHeaderContainer = useMemo(() => {
    const parent = document.querySelector(`#${state.replaceAll('-', '_')}_content`)
    if (!parent) return null

    // Use a dedicated element for the portal so React's removeChild operates on
    // a node only React manages, avoiding conflicts with Backbone DOM operations.
    const id = `${state.replaceAll('-', '_')}_sticky_header_portal`
    let el = parent.querySelector(`#${id}`)
    if (!el) {
      el = document.createElement('div')
      el.id = id
      el.style.display = 'contents'
      parent.prepend(el)
    }

    return el
  }, [state])

  return (
    <>
      {isReportXpert ? (
        <div className={style['survey-title-xpert']}>
          <div className={style['survey-title-xpert-icon']}>
            <i className="icon-aytmfd-xpert-solution-v2" />
          </div>
          <div className={style['survey-title-xpert-text']}>
            <div className={style['survey-title-xpert-text-title']}>
              <b>aytm Xpert</b>&nbsp;
              <span
                dangerouslySetInnerHTML={{ __html: customizations.find(v => 'Title' == v.title)?.value || '' }}
              ></span>
            </div>
            <div className={style['survey-title-report-text-report-title']}>
              <b>REPORT:</b>&nbsp;<span dangerouslySetInnerHTML={{ __html: report.title || 'Xpert Report' }}></span>
            </div>
          </div>
        </div>
      ) : (
        <div className={style['survey-container']}>
          <div className={style['survey-title']}>
            <span dangerouslySetInnerHTML={{ __html: surveyTitle }}></span>
            <div
              className={`${'charts' == state ? `${style.commandBar} ` : `${style.commandBarStatic} `}${
                style.commandBarContainer
              }`}
            >
              <KBarSpecialButton />
            </div>
          </div>

          {'charts' == state ? (
            <TutorialVideoList
              className={style.tutorialVideoList}
              targetClassName="pendo-help-stat-vid"
              video={StatTutorialVideo.get('video')}
              videoNote={StatTutorialVideo.get('videoNote')}
              videoPoints={StatTutorialVideo.get('videoPoints')}
              videoThumbnail={StatTutorialVideo.get('videoThumbnail')}
              hiddenText
            />
          ) : null}
        </div>
      )}

      {stickyHeaderContainer
        ? createPortal(
            <StickyHeader mode={mode} state={state} isReportXpert={isReportXpert} {...props} />,
            stickyHeaderContainer
          )
        : null}
    </>
  )
}

ContentHeader.propTypes = {
  mode: PropTypes.string,
  state: PropTypes.string,
  surveyTitle: PropTypes.string,
  surveyStatus: PropTypes.string,
  report: PropTypes.object,
  customizations: PropTypes.array,
  totals: PropTypes.object,
  controls: PropTypes.object,
  isReportLoading: PropTypes.bool,
  isReportVirtual: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  onFetch: PropTypes.func,
  onModeChange: PropTypes.func,
  onShowReportPopup: PropTypes.func
}

ContentHeader.displayName = 'ContentHeader'

export default ContentHeader
